import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.scale
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlin.random.Random

@Composable
fun Dot(
    modifier: Modifier = Modifier,
    color: Color = MaterialTheme.colorScheme.primary,
    size: Dp = 14.dp,
) {
    Box(
        modifier = modifier
            .clip(
                RoundedCornerShape(50)
            )
            .size(size)
            .background(color)
    )
}

@Composable
fun DiceBox(
    modifier: Modifier = Modifier,
    number: Int,
    fakeTimes: Int,
    pipColor: Color = MaterialTheme.colorScheme.primary,
    pipSize: Dp = 14.dp,
) {
    var currentNumber by remember { mutableStateOf(number) }

    LaunchedEffect(number) {
        // Perform the fake rolls
        repeat(fakeTimes) {
            currentNumber = Random.nextInt(1, 7)
            delay(100)  // Wait for a short delay between each roll
        }
        // Set the final number
        currentNumber = number
    }

    Box(modifier = modifier) {
        when (currentNumber) {
            1 -> {
                Dot(Modifier.align(Alignment.Center), pipColor, pipSize)
            }

            2 -> {
                Dot(Modifier.align(Alignment.TopStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomEnd), pipColor, pipSize)
            }

            3 -> {
                Dot(Modifier.align(Alignment.TopStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.Center), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomEnd), pipColor, pipSize)
            }

            4 -> {
                Dot(Modifier.align(Alignment.TopStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.TopEnd), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomEnd), pipColor, pipSize)
            }

            5 -> {
                Dot(Modifier.align(Alignment.TopStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.TopEnd), pipColor, pipSize)
                Dot(Modifier.align(Alignment.Center), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomEnd), pipColor, pipSize)
            }

            6 -> {
                Dot(Modifier.align(Alignment.TopStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.TopEnd), pipColor, pipSize)
                Dot(Modifier.align(Alignment.CenterEnd), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomStart), pipColor, pipSize)
                Dot(Modifier.align(Alignment.BottomEnd), pipColor, pipSize)
                Dot(Modifier.align(Alignment.CenterStart), pipColor, pipSize)
            }
        }
    }
}

/**
 * A single die face = base skin material + pips. The base skin may carry on-surface fire (the Ember
 * materials, via [DiceSkinStyle.innerFire]); a [DiceHat] adds an aura of flames rising off the top
 * that bleeds past the die. Shared by the in-game dice and the picker previews.
 */
@Composable
fun DieFace(
    number: Int,
    skin: DiceSkinStyle,
    hat: DiceHat,
    size: Dp,
    modifier: Modifier = Modifier,
    pipSize: Dp = 14.dp,
    pipPadding: Dp = 20.dp,
    cornerRadius: Dp = 18.dp,
    fakeTimes: Int = 5,
    animated: Boolean = true,
) {
    val twoDimensional by DiceDisplayPreferences.twoDimensional.collectAsState()
    if (twoDimensional) {
        DieFace2D(number, skin, hat, size, modifier, pipSize, pipPadding, cornerRadius, fakeTimes, animated)
    } else {
        Dice3D(number, size, rollProgress = 1f, variant = 0, modifier = modifier,
            skin = skin, hat = hat, animated = animated)
    }
}

@Composable
private fun DieFace2D(
    number: Int,
    skin: DiceSkinStyle,
    hat: DiceHat,
    size: Dp,
    modifier: Modifier = Modifier,
    pipSize: Dp = 14.dp,
    pipPadding: Dp = 20.dp,
    cornerRadius: Dp = 18.dp,
    fakeTimes: Int = 5,
    animated: Boolean = true,
) {
    Box(modifier = modifier.size(size)) {
        if (hat.palette != null) {
            FireHatBackGlow(
                palette = hat.palette,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .width(size * 1.16f)
                    .height(size * 0.9f)
                    .offset(y = -(size * 0.72f)),
            )
            FireDiceOverlay(
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .width(size * 1.16f)
                    .height(size * 0.92f)
                    .offset(y = -(size * 0.74f)),
                palette = hat.palette,
                shape = FireShape.PLUME,
                animated = animated,
            )
        }
        val dieShape = RoundedCornerShape(cornerRadius)
        Box(
            modifier = Modifier
                .matchParentSize()
                .shadow(14.dp, dieShape, ambientColor = Color.Black, spotColor = Color.Black)
                .clip(dieShape)
                .background(skin.faceBrush),
        ) {
            // Ember base materials fill the die surface with on-surface fire (under the pips).
            if (skin.innerFire != null) {
                FireDiceOverlay(Modifier.matchParentSize(), skin.innerFire, FireShape.FILL, animated)
            }
            if (hat.palette != null) {
                FireHatRimGlow(Modifier.matchParentSize(), hat.palette)
            }
            // Top gloss: light catching the upper edge for a rounded, polished read (under the pips
            // so they stay crisp).
            Box(
                Modifier
                    .matchParentSize()
                    .background(
                        brush = Brush.verticalGradient(
                            0f to Color.White.copy(alpha = 0.30f),
                            0.38f to Color.Transparent,
                            1f to Color.Black.copy(alpha = 0.10f),
                        ),
                        shape = dieShape,
                    ),
            )
            DiceBox(
                modifier = Modifier.matchParentSize().padding(pipPadding),
                number = number,
                fakeTimes = fakeTimes,
                pipColor = skin.pipColor,
                pipSize = pipSize,
            )
            // Beveled rim: bright top-left edge, soft dark bottom-right edge — a chiseled, 3D feel.
            Box(
                Modifier
                    .matchParentSize()
                    .border(
                        width = 1.5.dp,
                        brush = Brush.linearGradient(
                            0f to Color.White.copy(alpha = 0.65f),
                            0.5f to Color.Transparent,
                            1f to Color.Black.copy(alpha = 0.22f),
                        ),
                        shape = dieShape,
                    ),
            )
        }
    }
}

@Composable
private fun FireHatBackGlow(modifier: Modifier, palette: FirePalette) {
    val glow = palette.energyColor
    val core = palette.coreColor
    Canvas(modifier) {
        drawOval(
            color = glow.copy(alpha = 0.08f),
            topLeft = Offset(size.width * 0.12f, size.height * 0.25f),
            size = Size(size.width * 0.76f, size.height * 0.52f),
        )
        drawOval(
            color = glow.copy(alpha = 0.045f),
            topLeft = Offset(size.width * 0.26f, size.height * 0.04f),
            size = Size(size.width * 0.48f, size.height * 0.74f),
        )
        val sparkSize = size.width.coerceAtMost(size.height)
        drawCircle(
            color = core.copy(alpha = 0.5f),
            radius = sparkSize * 0.018f,
            center = Offset(size.width * 0.36f, size.height * 0.16f),
        )
        drawCircle(
            color = glow.copy(alpha = 0.38f),
            radius = sparkSize * 0.014f,
            center = Offset(size.width * 0.64f, size.height * 0.11f),
        )
        drawCircle(
            color = glow.copy(alpha = 0.28f),
            radius = sparkSize * 0.012f,
            center = Offset(size.width * 0.55f, size.height * 0.02f),
        )
    }
}

@Composable
private fun FireHatRimGlow(modifier: Modifier, palette: FirePalette) {
    val glow = palette.energyColor
    val core = palette.coreColor
    Canvas(modifier) {
        drawRect(
            brush = Brush.verticalGradient(
                0f to glow.copy(alpha = 0.28f),
                0.28f to glow.copy(alpha = 0.1f),
                1f to Color.Transparent,
                endY = size.height * 0.46f,
            ),
            size = size,
        )
        drawRoundRect(
            brush = Brush.horizontalGradient(
                0f to Color.Transparent,
                0.18f to glow.copy(alpha = 0.34f),
                0.5f to core.copy(alpha = 0.5f),
                0.82f to glow.copy(alpha = 0.34f),
                1f to Color.Transparent,
            ),
            topLeft = Offset(size.width * 0.14f, size.height * 0.02f),
            size = Size(size.width * 0.72f, size.height * 0.075f),
            cornerRadius = CornerRadius(size.height * 0.04f, size.height * 0.04f),
        )
    }
}

private val FirePalette.energyColor: Color
    get() = when (this) {
        FirePalette.BLUE -> Color(0xFF18B8FF)
        FirePalette.ORANGE -> Color(0xFFFF6A00)
    }

private val FirePalette.coreColor: Color
    get() = when (this) {
        FirePalette.BLUE -> Color(0xFFE8FBFF)
        FirePalette.ORANGE -> Color(0xFFFFF0B5)
    }

@Composable
fun DiceSection(
    showDice: Boolean = false,
    dieOneNumber: Int,
    dieTwoNumber: Int,
    animateDiceOnePosition: Dp,
    animateDiceTwoPosition: Dp,
    animateDiceScale: Float,
    modifier: Modifier,
    skin: DiceSkinStyle = DiceSkin.DEFAULT.style(),
    hat: DiceHat = DiceHat.NONE,
    dieOneRotation: Float = 0f,
    dieTwoRotation: Float = 0f,
    entryOffset: Dp = 0.dp,
    squash: Float = 0f,
    dieSize: Dp = DIE_SIZE,
    rollProgress: Float = 1f,
    useThreeDimensionalDice: Boolean = true,
) {
    // Pips and corner radius track the die size so a shrunk die (on short screens) stays proportional.
    val pipScale = dieSize / DIE_SIZE
    // Solid cubes retain their proportions through contact with the table.
    val renderedSquash = if (useThreeDimensionalDice) 0f else squash
    Row(
        modifier = modifier.fillMaxWidth().height(dieSize),
        horizontalArrangement = Arrangement.spacedBy(
            16.dp, Alignment.CenterHorizontally
        )
    ) {
        if (showDice && (dieOneNumber != 0 && dieTwoNumber != 0)) {
            val dieOneModifier = Modifier.offset(x = animateDiceOnePosition, y = entryOffset)
                .scale(
                    scaleX = animateDiceScale * (1f + renderedSquash),
                    scaleY = animateDiceScale * (1f - renderedSquash),
                )
                .rotate(dieOneRotation)
            val dieTwoModifier = Modifier.offset(x = animateDiceTwoPosition, y = entryOffset)
                .scale(
                    scaleX = animateDiceScale * (1f + renderedSquash),
                    scaleY = animateDiceScale * (1f - renderedSquash),
                )
                .rotate(dieTwoRotation)
            if (useThreeDimensionalDice) {
                Dice3D(
                    number = dieOneNumber,
                    size = dieSize,
                    rollProgress = rollProgress,
                    variant = 0,
                    modifier = dieOneModifier,
                    skin = skin,
                    hat = hat,
                )
                Dice3D(
                    number = dieTwoNumber,
                    size = dieSize,
                    rollProgress = rollProgress,
                    variant = 1,
                    modifier = dieTwoModifier,
                    skin = skin,
                    hat = hat,
                )
            } else {
                DieFace2D(
                    number = dieOneNumber,
                    skin = skin,
                    hat = hat,
                    size = dieSize,
                    pipSize = 18.dp * pipScale,
                    pipPadding = 26.dp * pipScale,
                    cornerRadius = 22.dp * pipScale,
                    modifier = dieOneModifier,
                )
                DieFace2D(
                    number = dieTwoNumber,
                    skin = skin,
                    hat = hat,
                    size = dieSize,
                    pipSize = 18.dp * pipScale,
                    pipPadding = 26.dp * pipScale,
                    cornerRadius = 22.dp * pipScale,
                    modifier = dieTwoModifier,
                )
            }
        }
    }
}

/**
 * The two dice at rest before the first roll of a round: a little faded and gently bobbing, so the
 * table reads as set for play rather than empty. Takes the same space as a rolled pair.
 */
@Composable
internal fun RestingDice(
    dieSize: Dp,
    skin: DiceSkinStyle,
    hat: DiceHat,
    useThreeDimensionalDice: Boolean,
) {
    val bob by rememberInfiniteTransition(label = "restingDice").animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(1600, easing = FastOutSlowInEasing), RepeatMode.Reverse),
        label = "restingDiceBob",
    )
    // Flat dice read larger than 3D ones at the same box size, so they draw a size smaller.
    val size = if (useThreeDimensionalDice) dieSize else dieSize * 0.8f
    val pipScale = size / DIE_SIZE
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(dieSize)
            .alpha(0.62f)
            .clearAndSetSemantics { },
        horizontalArrangement = Arrangement.spacedBy(size * 0.22f, Alignment.CenterHorizontally),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        listOf(Triple(5, -8f, bob), Triple(2, 6f, 1f - bob)).forEachIndexed { index, (value, tilt, lift) ->
            val dieModifier = Modifier
                .offset(y = -size * 0.05f * lift)
                .rotate(tilt)
            if (useThreeDimensionalDice) {
                Dice3D(
                    number = value,
                    size = size,
                    rollProgress = 1f,
                    variant = index,
                    modifier = dieModifier,
                    skin = skin,
                    hat = hat,
                )
            } else {
                DieFace2D(
                    number = value,
                    skin = skin,
                    hat = hat,
                    size = size,
                    pipSize = 18.dp * pipScale,
                    pipPadding = 26.dp * pipScale,
                    cornerRadius = 22.dp * pipScale,
                    modifier = dieModifier,
                )
            }
        }
    }
}

/** Size of an in-play die. The dice are the centerpiece of the game, so they're large and tactile. */
val DIE_SIZE: Dp = 132.dp
